<template>
  <section class="bg-plans">
    <div class="content">
      <div class="plans-header">
        <h1 class="title">{{ $t('message.plans') }}</h1>
        <p v-html="$t('message.plansIntro')" />
      </div>

      <div class="currency-toggle">
        <button
          :class="{ active: selectedCurrency === 'BRL' }"
          @click="selectedCurrency = 'BRL'"
        >
          BRL
        </button>
        <button
          :class="{ active: selectedCurrency === 'USD' }"
          @click="selectedCurrency = 'USD'"
        >
          USD
        </button>
      </div>

      <Splide
        :options="splideOptions"
        class="plans-carousel"
        v-if="shouldShowCarousel"
      >
        <SplideSlide
          v-for="plan in plansData"
          :key="plan.id"
        >
          <p
            class="plan-detailed-description"
            v-html="$t(plan.descriptionKey)"
          />

          <div class="plan">
            <h1 class="plan-header">{{ $t(plan.nameKey) }}</h1>
            <div class="plan-description">
              <ul class="plan-features">
                <li
                  v-for="(featureKey, index) in plan.featureKeys"
                  :key="index"
                >
                  <p>{{ $t(featureKey) }}</p>
                </li>
              </ul>
            </div>
            <div class="card-footer">
              <template v-if="plan.isOnDemand">
                <span class="on-demand-label">
                  {{ $t('message.contactUs') }}
                </span>
                <span class="on-demand-description">
                  {{ $t('message.onDemandPlan') }}
                </span>
              </template>
              <template v-else>
                <span class="call-to-action">{{ $t('message.by') }}</span>
                <span>
                  <div class="price">
                    {{ formatPrice(plan.id, selectedCurrency) }}
                  </div>
                  <span>{{ $t('message.monthlyInvestment') }}</span>
                </span>
              </template>
            </div>
            <div class="plan-footer">
              <a
                :href="getPlanActionHref(plan)"
                :target="plan.isOnDemand ? null : '_blank'"
                class="btn btn-gradient"
              >
                {{
                  plan.isOnDemand
                    ? $t('message.contactUs')
                    : $t('message.subscribeNow')
                }}
              </a>
            </div>
          </div>
        </SplideSlide>
      </Splide>

      <div
        v-else
        class="plans-grid"
      >
        <div
          v-for="plan in plansData"
          :key="plan.id"
          class="plan"
        >
          <h1 class="plan-header">{{ $t(plan.nameKey) }}</h1>
          <!-- <SeeMore>
            <p
              class="plan-detailed-description"
              v-html="$t(plan.descriptionKey)"
            />
          </SeeMore> -->
          <p
            class="plan-detailed-description"
            v-html="$t(plan.descriptionKey)"
          />
          <div class="plan-description">
            <ul class="plan-features">
              <li
                v-for="(featureKey, index) in plan.featureKeys"
                :key="index"
              >
                <p>{{ $t(featureKey) }}</p>
              </li>
            </ul>
          </div>
          <div class="card-footer">
            <template v-if="plan.isOnDemand">
              <span class="on-demand-label">
                {{ $t('message.contactUs') }}
              </span>
              <span class="on-demand-description">
                {{ $t('message.onDemandPlan') }}
              </span>
            </template>
            <template v-else>
              <span class="call-to-action">{{ $t('message.by') }}</span>
              <span>
                <div class="price">
                  {{ formatPrice(plan.id, selectedCurrency) }}
                </div>
                <span>{{ $t('message.monthlyInvestment') }}</span>
              </span>
            </template>
          </div>
          <div class="plan-footer">
            <a
              :href="getPlanActionHref(plan)"
              :target="plan.isOnDemand ? null : '_blank'"
              class="btn btn-gradient"
            >
              {{
                plan.isOnDemand
                  ? $t('message.contactUs')
                  : $t('message.subscribeNow')
              }}
            </a>
          </div>
        </div>
      </div>
    </div>
    <div class="plan-details-footer">
      <span>
        <span v-html="$t('message.explanation')" />
        <span>{{ $t('message.fixedValueDisclaimer') }}</span>
        <span>{{ $t('message.referentialValueDisclaimer') }}</span>
      </span>
    </div>
  </section>
</template>

<script>
import { Splide, SplideSlide } from '@splidejs/vue-splide'
import '@splidejs/splide/dist/css/themes/splide-default.min.css'
// import SeeMore from './SeeMore/SeeMore.vue'

// Define constants for better readability and maintainability
const CACHE_KEY_LOCATION = 'user_location_brazil'
const CACHE_KEY_TTL = 'user_location_brazil_ttl'
const CACHE_TTL_MS = 1000 * 60 * 60 * 12 // 12 hours
const REGISTER_URL = 'https://financeiro.janelaunica.com.br/#/registro'
const CONTACT_URL = '/contact'

export default {
  name: 'PlansComponent',
  components: {
    Splide,
    SplideSlide,
    // SeeMore,
  },
  data() {
    return {
      selectedCurrency: 'BRL', // Default currency
      isBrazilUser: true, // Will be updated by checkUserLocation
      splideOptions: {
        type: 'loop',
        perPage: 3,
        breakpoints: {
          992: {
            perPage: 1,
          },
        },
        arrows: true,
        pagination: true,
      },
      plansData: [
        {
          id: 'basic',
          nameKey: 'message.planBasic',
          featureKeys: [
            'message.basicFeature1',
            'message.basicFeature2',
            'message.basicFeature3',
            'message.basicFeature4',
            'message.basicFeature5',
            'message.basicFeature6',
            'message.basicFeature7',
          ],
          descriptionKey: 'message.basicDescription',
          prices: {
            BRL: 'R$74,90 /mês',
            USD: '$14.90 /mês',
          },
        },
        {
          id: 'standard',
          nameKey: 'message.planStandard',
          featureKeys: [
            'message.standardFeature1',
            'message.standardFeature2',
            'message.standardFeature3',
            'message.standardFeature4',
            'message.standardFeature5',
            'message.standardFeature6',
            'message.standardFeature7',
          ],
          descriptionKey: 'message.standardDescription',
          prices: {
            BRL: 'R$94,90 /mês',
            USD: '$22.90 /mês',
          },
        },
        {
          id: 'premium',
          nameKey: 'message.planPremium',
          featureKeys: [
            'message.premiumFeature1',
            'message.premiumFeature2',
            'message.premiumFeature3',
            'message.premiumFeature4',
            'message.premiumFeature5',
            'message.premiumFeature6',
            'message.premiumFeature7',
          ],
          descriptionKey: 'message.premiumDescription',
          isOnDemand: true,
        },
      ],
      windowWidth: window.innerWidth,
    }
  },
  computed: {
    shouldShowCarousel() {
      const numberOfPlans = this.plansData.length
      let perPage = this.splideOptions.perPage

      if (this.windowWidth < 992) {
        perPage = this.splideOptions.breakpoints['992'].perPage
      }
      return numberOfPlans > perPage
    },
  },
  methods: {
    async checkUserLocation() {
      const cachedIsBrazil = localStorage.getItem(CACHE_KEY_LOCATION)
      const cachedExpiresAt = localStorage.getItem(CACHE_KEY_TTL)
      const now = Date.now()

      if (
        cachedIsBrazil !== null &&
        cachedExpiresAt !== null &&
        now < parseInt(cachedExpiresAt)
      ) {
        this.isBrazilUser = cachedIsBrazil === 'true'
        this.selectedCurrency = this.isBrazilUser ? 'BRL' : 'USD'
        return
      }

      try {
        const response = await fetch(
          'https://ipinfo.io/json?token=b9ec19d5af638e'
        )
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const data = await response.json()
        this.isBrazilUser = data.country === 'BR'
        this.selectedCurrency = this.isBrazilUser ? 'BRL' : 'USD'

        localStorage.setItem(CACHE_KEY_LOCATION, this.isBrazilUser.toString())
        localStorage.setItem(CACHE_KEY_TTL, (now + CACHE_TTL_MS).toString())
      } catch (error) {
        console.error('Erro ao verificar a localização do usuário:', error)
        this.isBrazilUser = true
        this.selectedCurrency = 'BRL'
        localStorage.setItem(CACHE_KEY_LOCATION, 'true')
        localStorage.setItem(CACHE_KEY_TTL, (now + CACHE_TTL_MS).toString())
      }
    },
    formatPrice(planId, currency) {
      const plan = this.plansData.find((p) => p.id === planId)
      return plan?.prices ? plan.prices[currency] : 'N/A'
    },
    getPlanActionHref(plan) {
      return plan.isOnDemand ? CONTACT_URL : REGISTER_URL
    },
    updateWindowWidth() {
      this.windowWidth = window.innerWidth
    },
  },
  mounted() {
    this.checkUserLocation()
    window.addEventListener('resize', this.updateWindowWidth)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateWindowWidth)
  },
}
</script>

<style scoped>
/* GENERAL STYLES */
.bg-plans {
  padding: 24px;
  background: var(--c5);
}

.content {
  max-width: 1200px; /* Limit content width for better readability */
  margin: 0 auto; /* Center the content */
}

/* HEADER */
.plans-header {
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

#logo {
  margin-bottom: 15px;
  width: 250px;
}

.plans-header p {
  color: var(--c3);
  max-width: 700px; /* Limit paragraph width */
}

/* CURRENCY TOGGLE */
.currency-toggle {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.currency-toggle button {
  background-color: var(--c2);
  border: 1px solid var(--c6);
  padding: 8px 16px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.3s ease, color 0.3s ease,
    border-color 0.3s ease;
}

.currency-toggle button:first-child {
  border-radius: 5px 0 0 5px;
}

.currency-toggle button:last-child {
  border-radius: 0 5px 5px 0;
}

.currency-toggle button.active {
  background-color: var(--color-primary);
  color: white;
  border-color: var(--color-primary);
}

/* PLAN CARD */
.plan {
  margin: 0 12px 24px 12px; /* Add horizontal margin for spacing */
  background-color: var(--c4);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  padding-bottom: 13px;
  box-sizing: border-box; /* Include padding in height */
}

.plan-header {
  display: flex;
  justify-content: center; /* Center the plan name */
  width: 100%;
  align-items: center;
  padding: 12px;
  box-sizing: border-box;
  border-bottom: 2px solid var(--c6);
  border-radius: 8px 8px 0 0;
  text-align: center;
  margin-bottom: -5px;
}

.plan-header > h1 {
  margin-bottom: 0px;
  color: var(--c1); /* Consistent color */
}

/* FEATURES LIST */
.plan-features {
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  padding: 0 20px; /* Add some padding to the list */
  box-sizing: border-box;
}

.plan-features li {
  position: relative;
  margin-bottom: 8px;
  color: var(--c3);
  padding-left: 30px; /* Space for the custom bullet */
  text-align: left;
}

.plan-features li p {
  margin: 0;
}

.plan-features li::before {
  content: '';
  display: block;
  position: absolute;
  border-radius: 50%; /* Use 50% for a perfect circle */
  width: 10px;
  height: 10px;
  left: 10px; /* Position the custom bullet */
  top: 50%;
  transform: translateY(-50%);
  background: var(--gradient-primary);
}

.plan-detailed-description {
  color: var(--c3);
  font-size: 0.9rem;
  line-height: 1.5;
  padding: 0 20px;
  text-align: left; /* Align description text to the left */
}

/* CARD FOOTER (PRICE SECTION) */
.card-footer {
  display: flex;
  flex-direction: column; /* Stack price elements vertically */
  width: 100%;
  align-items: center; /* Center items horizontally */
  padding: 12px;
  box-sizing: border-box;
  border-top: 2px solid var(--c6); /* Use border-top for better visual flow */
  border-radius: 0 0 8px 8px; /* Apply border-radius to bottom corners */
  margin-top: auto; /* Push to the bottom */
}

.call-to-action,
.on-demand-description {
  font-size: small;
  color: var(--c1);
  margin-bottom: 5px; /* Adjust spacing */
}

.on-demand-label {
  color: var(--color-primary);
  font-size: 1.9rem;
  font-weight: bold;
  margin-bottom: 5px;
  text-align: center;
}

.on-demand-description {
  color: var(--c6);
  font-size: 0.9rem;
  text-align: center;
}

.price {
  /* Renamed for clarity */
  color: var(--color-primary);
  font-size: 2.2rem; /* Slightly larger for emphasis */
  font-weight: bold; /* Make it bold */
  margin-bottom: 5px; /* Adjust spacing */
}

.card-footer span > span {
  /* For "investimento mensal" */
  font-size: 0.85rem; /* Slightly larger */
  text-align: center; /* Center the text */
  color: var(--c6);
}

/* PLAN DETAILS FOOTER */
.plan-details-footer {
  /* Renamed for clarity */
  display: flex;
  flex-direction: column; /* Stack explanations vertically */
  margin: 24px auto 0 auto; /* Adjust margin for spacing */
  max-width: 900px; /* Limit width */
  padding: 0 15px; /* Add some horizontal padding */
}

.plan-details-footer > span {
  display: flex;
  flex-direction: column;
  margin-bottom: 10px; /* Space between explanation blocks */
}

.plan-details-footer span > span {
  font-size: 0.8rem; /* Slightly larger and more readable */
  line-height: 1.4; /* Improve line spacing */
  color: var(--c3);
  margin-bottom: 5px; /* Space between individual explanation lines */
}

/* SPLIDE OVERRIDES/ADJUSTMENTS */
@media (min-width: 992px) {
  .splide__track {
    padding: 10px 0; /* Add vertical padding to track */
  }
  .splide__slide {
    display: flex;
    justify-content: center;
    align-items: stretch; /* Ensure all slides stretch to the same height */
  }
  .plan {
    width: 95%; /* Adjust width within slide for better spacing */
    margin: 0 auto; /* Center the plan within the slide */
  }
}

/* Styles for the non-carousel grid layout */
.plans-grid {
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(300px, 1fr)
  ); /* Adjust as needed */
  gap: 24px; /* Space between plans */
  justify-items: center; /* Center items in the grid */
  align-items: stretch; /* Make sure all plans have the same height */
  margin: 0 auto;
}

.plans-grid .plan {
  margin: 0; /* Remove specific margins from individual plans when in grid */
}

@media (max-width: 700px) {
  #logo {
    width: 200px;
    margin-top: 0;
  }
  .plans-header p {
    padding: 0 15px; /* Add padding to paragraph on small screens */
  }
  /* Remove specific plan margin when it's a single column in grid */
  .plans-grid .plan {
    padding: 0 15px; /* Add internal padding */
  }
  .plan-features li {
    padding-left: 25px; /* Adjust padding for smaller screens */
  }
  .plan-features li::before {
    left: 5px; /* Adjust bullet position */
  }
  .card-footer,
  .plan-details-footer {
    padding: 10px 15px; /* Adjust padding */
  }
}
</style>
